import React, { useEffect, useState, useMemo, useRef } from "react";

import { MdAccessTime, MdCheckCircleOutline, MdErrorOutline } from "react-icons/md";

import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";

import axios from "axios";

import { motion, AnimatePresence } from "framer-motion";

import { QRCodeCanvas } from "qrcode.react";

import { db } from "../../utils/firebase";

import logoLight from "../../assets/logo1.png";

import hiklogo from "../../assets/hikvision.png";

import dahuaLogo from "../../assets/dahuaLogo.png";

import ztkeco from "../../assets/zkteco.png";



const PAYMENT_API = (process.env.REACT_APP_PAYMENT_API_BASE ||

    `https://us-central1-${db.app?.options?.projectId || "iparking-55511"}.cloudfunctions.net`)

    .replace(/\/$/, "");

const formatTimestamp = (value) => {

    if (value == null) return "—";

    const ms =

        typeof value.toMillis === "function"

            ? value.toMillis()

            : typeof value.seconds === "number"

              ? value.seconds * 1000

              : new Date(value).getTime();

    return Number.isFinite(ms)

        ? new Intl.DateTimeFormat("mn-MN", {

              year: "numeric",

              month: "2-digit",

              day: "2-digit",

              hour: "2-digit",

              minute: "2-digit",

              hour12: false,

              timeZone: "Asia/Ulaanbaatar",

          }).format(ms)

        : "—";

};

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function preparePayment(identity, intent, signal) {

    if (!identity.parkId || (!identity.recordId && !identity.plate)) {

        throw new Error("Зогсоолын бүртгэлийн мэдээлэл дутуу байна.");

    }

    let lastError;

    for (let attempt = 0; attempt < 12; attempt += 1) {

        try {

            const response = await axios.post(

                `${PAYMENT_API}/prepareParkingPaymentSafeV2`,

                {

                    parkId: identity.parkId,

                    // Preserve recordId for compatibility; the backend verifies

                    // the plate and chooses the latest open entry_time.

                    recordId: identity.recordId,

                    plate: identity.plate,

                    intent,

                },

                { timeout: 25000, signal },

            );

            if (response.data?.kind === "creating") {

                await pause(1500);

                continue;

            }

            if (response.data?.kind === "reconciliation") {

                const error = new Error(response.data.message);

                error.code = "PAYMENT_RECONCILIATION_REQUIRED";

                throw error;

            }

            if (response.data?.ok !== true || !response.data.record)

                throw new Error("Төлбөрийн хариу дутуу байна.");

            return response.data;

        } catch (error) {

            lastError = error;

            const status = error.response?.status;

            if (signal?.aborted || error.code === "ERR_CANCELED") throw error;

            // Diagnostics only: never log credentials, tokens or full request bodies.

            console.warn("[iParking] prepareParkingPayment failed", {

                intent,

                attempt: attempt + 1,

                httpStatus: status ?? null,

                code: error.response?.data?.code || error.code || "UNKNOWN",

                message: error.response?.data?.message || error.message || "No error message",

            });

            if (

                error.code === "PAYMENT_RECONCILIATION_REQUIRED" ||

                intent === "pay" || // never issue another payment write on unknown network result

                (status && status < 500) ||

                attempt >= 2

            )

                throw error;

            await pause(1500);

        }

    }

    throw lastError || new Error("Нэхэмжлэл үүсгэж байна. Түр хүлээгээд дахин шалгана уу.");

}

const paymentError = (error) => {

    const message =

        error?.response?.data?.message ||

        (error?.code === "ERR_NETWORK"

            ? "Төлбөрийн сервертэй холболт тогтоосонгүй (CORS, Firebase endpoint эсвэл сүлжээ). Дахин төлөхөөс өмнө өмнөх нэхэмжлэлийг шалгана уу."

            : error?.message) ||

        "Төлбөрийг шалгаж чадсангүй. Дахин төлөхөөс өмнө төлбөрийн төлөвөө шалгана уу.";

    const code = error?.response?.data?.code || error?.code || null;

    const httpStatus = error?.response?.status || null;

    const details = [code && `Код: ${code}`, httpStatus && `HTTP ${httpStatus}`]

        .filter(Boolean)

        .join(" · ");

    return details ? `${message} (${details})` : message;

};



const ParkingPayment = () => {

    const [searchParams] = useSearchParams();

    const { id, parkId: routeParkId } = useParams();

    const location = useLocation();

    const navigate = useNavigate();

    const identity = useMemo(() => {

        const get = (key) => location.state?.[key] ?? searchParams.get(key);

        return {

            parkId: id || routeParkId,

            recordId: get("recordId"),

            plate: get("plate") || "",

        };

    }, [location.state, searchParams, id, routeParkId]);

    const contextKey = JSON.stringify([identity.parkId, identity.recordId, identity.plate]);

    const [details, setDetails] = useState({

        gracePeriod: 15,

        ebarimt: false,

        duration: "",

        paidUntil: null,

    });

    const config = { ...identity, ...details };

    const [status, setStatus] = useState({

        loading: true,

        invoiceLoading: true,

        error: null,

        notice: null,

        complete: false,

        isFree: false,

    });

    const [recordData, setRecordData] = useState({

        id: null,

        contextKey: null,

        entryTime: "",

        exitTime: "",

        plateNumber: "",

        isLarge: false,

        currentPrice: 0,

    });

    const [payment, setPayment] = useState({

        invoice: null,

        banks: [],

        mainQr: "",

    });

    const [checkRequest, setCheckRequest] = useState(0);

    const [initializationRetry, setInitializationRetry] = useState(0);

    const [paymentRequest, setPaymentRequest] = useState(null);

    // PlateScreen explicitly confirms a full plate and passes autoPay=true.
    // Only that flow starts the Minu invoice automatically on mount;
    // direct links retain the existing safe preview-first behavior.
    const autoPay = location.state?.autoPay === true && Boolean(identity.recordId && identity.plate);
    // If a write times out, retry only a READ-style preview first; never replay
    // a potentially committed invoice request on the error-screen Retry button.
    const [safeCheckContext, setSafeCheckContext] = useState(null);
    const paymentIntent = safeCheckContext === contextKey
        ? "preview"
        : autoPay || paymentRequest === contextKey ? "pay" : "preview";

    const initialization = useRef(null);

    const pollInFlight = useRef(false);

    const currentLogo = logoLight;



    useEffect(() => {

        let mounted = true;

        setStatus({

            loading: true,

            invoiceLoading: true,

            error: null,

            notice: null,

            complete: false,

            isFree: false,

        });

        setPayment({ invoice: null, banks: [], mainQr: "" });

        const taskKey = JSON.stringify([contextKey, initializationRetry, paymentIntent]);

        if (initialization.current?.key !== taskKey) {

            // React StrictMode remounts effects. Single-flight + abort stale reads.

            if (initialization.current?.controller && initialization.current.intent === "preview")

                initialization.current.controller.abort();

            const controller = new AbortController();

            initialization.current = {

                key: taskKey,

                intent: paymentIntent,

                controller,

                promise: preparePayment(identity, paymentIntent, controller.signal),

            };

        }

        initialization.current.promise

            .then((result) => {

                if (!mounted) return;

                setRecordData({

                    id: result.record.id,

                    contextKey,

                    entryTime: formatTimestamp(result.record.entryTime),

                    exitTime: formatTimestamp(result.asOf),

                    plateNumber: result.record.plate,

                    isLarge: result.record.isLarge,

                    currentPrice: result.amount,

                });

                setDetails({

                    gracePeriod: result.graceMinutes,

                    ebarimt: result.ebarimt,

                    duration: result.record.duration,

                    paidUntil: result.paidUntil,

                });

                setPayment({

                    invoice: result.invoice,

                    banks: result.invoice?.banks || [],

                    mainQr: result.invoice?.mainQr || "",

                });

                setStatus({

                    loading: false,

                    invoiceLoading: false,

                    error: null,

                    complete: ["paid", "completed", "free"].includes(result.kind),

                    isFree: result.kind === "free",

                    notice:

                        result.message ||

                        (result.invoice?.legacy

                            ? "Өмнө үүсгэсэн нэхэмжлэлийг шалгаж байна. Дахин төлөхгүйгээр өмнөх QR-ийн төлөвийг шалгана уу."

                            : null),

                });

            })

            .catch((error) => {

                if (mounted && error?.code !== "ERR_CANCELED")

                    setStatus((prev) => ({

                        ...prev,

                        loading: false,

                        invoiceLoading: false,

                        error: paymentError(error),

                    }));

            });

        return () => {

            mounted = false;

        };

    }, [identity, contextKey, initializationRetry, paymentIntent]);



    // An abandoned fee lookup never becomes a payment reservation. Refresh it

    // on return to the page and periodically while it remains on screen.

    useEffect(() => {

        if (paymentIntent !== "preview" || payment.invoice || status.complete || status.error) return;

        const refresh = () => setInitializationRetry((value) => value + 1);

        const timer = setInterval(refresh, 30000);

        window.addEventListener("focus", refresh);

        return () => {

            clearInterval(timer);

            window.removeEventListener("focus", refresh);

        };

    }, [paymentIntent, payment.invoice, status.complete, status.error]);



    useEffect(() => {

        const invoice = payment.invoice;

        if (!invoice?.invoiceNumber || status.complete || recordData.contextKey !== contextKey)

            return;

        let mounted = true,

            stopped = false;

        const check = async () => {

            if (stopped || pollInFlight.current) return;

            pollInFlight.current = true;

            try {

                const response = await axios.post(

                    `${PAYMENT_API}/checkParkingPaymentSafeV2`,

                    {

                        parkId: identity.parkId,

                        recordId: recordData.id,

                        referenceNumber: invoice.referenceNumber,

                        chargeVersion: invoice.chargeVersion,

                    },

                    { timeout: 25000 },

                );

                if (!mounted) return;

                const result = response.data;

                if (result?.kind === "reconciliation") {

                    stopped = true;

                    setPayment((prev) => ({ ...prev, banks: [], mainQr: "", needsRefresh: true }));

                    setStatus((prev) => ({

                        ...prev,

                        notice: result.message || "Төлбөрийн бүртгэлийг ажилтнаар тулгуулна уу.",

                        complete: false,

                    }));

                } else if (result?.kind === "price_changed") {

                    setRecordData((prev) => ({ ...prev, currentPrice: result.amount }));

                    setDetails((prev) => ({ ...prev, duration: result.record.duration }));

                    // Keep polling the earlier invoice even while its QR is

                    // hidden: the bank app may still be completing that payment.

                    setPayment((prev) => ({ ...prev, banks: [], mainQr: "", needsRefresh: true }));

                    setStatus((prev) => ({ ...prev, notice: result.message }));

                } else if (["paid", "completed"].includes(result?.kind)) {

                    setRecordData((prev) => ({ ...prev, currentPrice: result.amount }));

                    setDetails((prev) => ({

                        ...prev,

                        gracePeriod: result.graceMinutes,

                        paidUntil: result.paidUntil,

                    }));

                    setStatus((prev) => ({

                        ...prev,

                        complete: true,

                        isFree: false,

                        error: null,

                        notice: null,

                    }));

                }

            } catch (error) {

                if (!mounted) return;

                if (error.response?.status >= 400 && error.response.status < 500) {

                    stopped = true;

                    // An existing invoice must remain recoverable; don't replace QR

                    // with an all-screen error or imply the payment is unpaid.

                    setPayment((prev) => ({ ...prev, banks: [], mainQr: "", needsRefresh: true }));

                    setStatus((prev) => ({

                        ...prev,

                        notice: paymentError(error),

                        complete: false,

                    }));

                } else

                    setStatus((prev) => ({

                        ...prev,

                        notice: "Төлбөрийн төлөвийг дахин шалгаж байна. Төлсөн бол дахин төлөх шаардлагагүй.",

                    }));

            } finally {

                pollInFlight.current = false;

            }

        };

        check();

        const timer = setInterval(check, 5000);

        window.addEventListener("focus", check);

        return () => {

            mounted = false;

            clearInterval(timer);

            window.removeEventListener("focus", check);

        };

    }, [

        payment.invoice,

        recordData.id,

        recordData.contextKey,

        identity.parkId,

        contextKey,

        status.complete,

        checkRequest,

    ]);



    // ----------------------------------------------------

    // 🎨 UI Rendering — Modern Color Premium

    // ----------------------------------------------------



    const UI_FONT = '"Noto Sans", Inter, "Segoe UI", Arial, sans-serif';

    const TITLE_FONT = '"Arial", "Noto Sans", "Segoe UI", sans-serif';



    const formatPrice = (value) => `${Number(value || 0).toLocaleString("mn-MN")}₮`;



    const bankTone = () =>

        "tw-border-slate-200 tw-bg-[#FBFCFD] hover:tw-border-[#A9D5C9] hover:tw-bg-[#F8FCFA]";



    const AppFooter = () => (

        <div className="tw-mt-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-[7.5px] tw-font-medium tw-tracking-[0.03em] tw-text-slate-400">

            <span>iParking</span>

            <span className="tw-h-1 tw-w-1 tw-rounded-full tw-bg-slate-300" />

            <span>Smart Parking System</span>

        </div>

    );



    const PartnerStrip = () => (

        <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-border-t tw-border-white/[0.08] tw-pt-3">

            <img

                src={hiklogo}

                alt="Hikvision"

                className="tw-h-[14px] tw-w-[48px] tw-object-contain tw-opacity-65"

            />

            <span className="tw-h-3 tw-w-px tw-bg-white/10" />

            <img

                src={dahuaLogo}

                alt="Dahua"

                className="tw-h-[14px] tw-w-[46px] tw-object-contain tw-opacity-65"

            />

            <span className="tw-h-3 tw-w-px tw-bg-white/10" />

            <img

                src={ztkeco}

                alt="ZKTeco"

                className="tw-h-[14px] tw-w-[44px] tw-object-contain tw-opacity-65"

            />

        </div>

    );



    // ----------------------------------------------------

    // ✅ ТӨЛБӨР АМЖИЛТТАЙ / ҮНЭГҮЙ

    // ----------------------------------------------------

    if (status.complete) {

        return (

            <motion.div

                className="tw-fixed tw-inset-0 tw-z-[99999] tw-overflow-y-auto tw-overscroll-contain tw-bg-[#F1F6F5]"

                style={{

                    fontFamily: UI_FONT,

                    backgroundImage:

                        "radial-gradient(circle at 50% -10%, rgba(255,255,255,0.95) 0%, rgba(238,241,244,0) 48%)",

                }}

                initial={{ opacity: 0 }}

                animate={{ opacity: 1 }}

            >

                <div className="tw-min-h-full tw-w-full tw-px-3 tw-py-3.5 sm:tw-flex sm:tw-items-center sm:tw-justify-center sm:tw-px-5 sm:tw-py-7">

                    <main className="tw-mx-auto tw-w-full tw-max-w-[420px]">

                        <section className="tw-overflow-hidden tw-rounded-[28px] tw-border tw-border-white tw-bg-white tw-shadow-[0_18px_65px_rgba(16,42,50,0.1)]">

                            <div className="tw-h-[2px] tw-w-full tw-bg-[#07966E]" />



                            <header

                                className="tw-px-5 tw-pt-4 tw-pb-6 min-[390px]:tw-px-6"

                                style={{

                                    background: "#102A35",

                                }}

                            >

                                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">

                                    <img

                                        src={currentLogo}

                                        alt="iParking"

                                        className="tw-w-[102px] tw-object-contain"

                                    />



                                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-emerald-300/20 tw-bg-emerald-300/10 tw-px-2.5 tw-py-1.5">

                                        <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-300 tw-shadow-[0_0_8px_rgba(110,231,183,0.8)]" />

                                        <span className="tw-text-[8.5px] tw-font-semibold tw-text-emerald-100">

                                            Амжилттай

                                        </span>

                                    </div>

                                </div>



                                <div className="tw-mt-5 tw-text-center">

                                    <motion.div

                                        initial={{ scale: 0.88, opacity: 0 }}

                                        animate={{ scale: 1, opacity: 1 }}

                                        className="tw-mx-auto tw-flex tw-h-[58px] tw-w-[58px] tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-400/10 tw-text-emerald-300"

                                    >

                                        <MdCheckCircleOutline className="tw-text-[32px]" />

                                    </motion.div>



                                    <h1

                                        className="tw-mt-4 tw-mb-0 tw-text-[25px] min-[390px]:tw-text-[27px] tw-font-normal tw-leading-[1.15] tw-tracking-[-0.02em] tw-text-white"

                                        style={{ fontFamily: TITLE_FONT }}

                                    >

                                        {status.isFree ? "Таны төлбөр 0₮" : "Төлбөр амжилттай"}

                                    </h1>



                                    <p className="tw-mx-auto tw-mt-2 tw-mb-0 tw-max-w-[285px] tw-text-[10.5px] tw-leading-[1.6] tw-text-slate-400">

                                        {status.isFree

                                            ? "Таны зогсоолын төлбөр үнэгүй байна."

                                            : config.paidUntil

                                              ? `Төлбөрийн гарах эрх ${formatTimestamp(config.paidUntil)} хүртэл хүчинтэй.`

                                              : `Төлбөр баталгаажсан цагаас ${config.gracePeriod} минутын дотор зогсоолоос гарна уу.`}

                                    </p>

                                </div>



                                <PartnerStrip />

                            </header>



                            <div className="tw-p-3.5 min-[390px]:tw-p-[18px]">

                                <div className="tw-rounded-[19px] tw-border tw-border-slate-200/90 tw-bg-white tw-p-4 min-[390px]:tw-p-[18px]">

                                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">

                                        <div>

                                            <span className="tw-block tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-[0.09em] tw-text-slate-400">

                                                Улсын дугаар

                                            </span>

                                            <span className="tw-mt-1 tw-block tw-text-[26px] tw-font-extrabold tw-tracking-[0.04em] tw-text-[#101828]">

                                                {recordData.plateNumber}

                                            </span>

                                        </div>



                                        <span className="tw-rounded-full tw-bg-emerald-100 tw-px-2.5 tw-py-1 tw-text-[8px] tw-font-bold tw-text-emerald-700">

                                            {status.isFree ? "Үнэгүй" : "Төлөгдсөн"}

                                        </span>

                                    </div>



                                    <div className="tw-my-4 tw-h-px tw-bg-slate-100" />



                                    <div className="tw-flex tw-items-end tw-justify-between tw-gap-4">

                                        <span className="tw-text-[10px] tw-font-medium tw-text-slate-500">

                                            Нийт дүн

                                        </span>



                                        <span className="tw-text-[32px] tw-font-extrabold tw-tracking-[-0.04em] tw-text-[#101828]">

                                            {status.isFree

                                                ? "0₮"

                                                : formatPrice(recordData.currentPrice)}

                                        </span>

                                    </div>



                                    <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-2">

                                        <div className="tw-rounded-[12px] tw-bg-[#F8FAFC] tw-px-3 tw-py-2.5">

                                            <span className="tw-block tw-text-[8px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-400">

                                                Огноо

                                            </span>

                                            <span className="tw-mt-1 tw-block tw-text-[9.5px] tw-font-medium tw-leading-4 tw-text-slate-700">

                                                {recordData.exitTime}

                                            </span>

                                        </div>



                                        <div className="tw-rounded-[12px] tw-bg-[#F8FAFC] tw-px-3 tw-py-2.5">

                                            <span className="tw-block tw-text-[8px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-emerald-600">

                                                Төрөл

                                            </span>

                                            <span className="tw-mt-1 tw-block tw-text-[9.5px] tw-font-medium tw-leading-4 tw-text-slate-700">

                                                {recordData.isLarge ? "Том оврын" : "Суудлын"}

                                            </span>

                                        </div>

                                    </div>

                                </div>



                                <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2.5">

                                    {config.ebarimt === true && !status.isFree && (

                                        <motion.button

                                            whileTap={{ scale: 0.98 }}

                                            className="tw-h-12 tw-w-full tw-rounded-[14px] tw-bg-[#102A35] tw-text-[12px] tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#162033]"

                                            onClick={() =>

                                                navigate("/ebarimt", {

                                                    state: {

                                                        parkId: config.parkId,

                                                        recordId: recordData.id,

                                                        price: recordData.currentPrice,

                                                        plate: recordData.plateNumber,

                                                        title: "iParking Зогсоол",

                                                    },

                                                })

                                            }

                                        >

                                            И-Баримт авах

                                        </motion.button>

                                    )}



                                    <button

                                        type="button"

                                        onClick={() => navigate(`/plate/${config.parkId}`)}

                                        className="tw-h-11 tw-w-full tw-rounded-[13px] tw-border tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-semibold tw-text-slate-600 tw-transition hover:tw-border-blue-200 hover:tw-bg-blue-50/40 active:tw-scale-[0.99]"

                                    >

                                        Буцах

                                    </button>

                                </div>



                                <AppFooter />

                            </div>

                        </section>

                    </main>

                </div>

            </motion.div>

        );

    }



    // ----------------------------------------------------

    // ⏳ УНШИЖ БАЙГАА / АЛДАА

    // ----------------------------------------------------

    if (status.loading || status.error) {

        return (

            <div

                className="tw-fixed tw-inset-0 tw-z-[99999] tw-overflow-y-auto tw-bg-[#F1F6F5]"

                style={{

                    fontFamily: UI_FONT,

                    backgroundImage:

                        "radial-gradient(circle at 50% -10%, rgba(255,255,255,0.95) 0%, rgba(238,241,244,0) 48%)",

                }}

            >

                <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-3 sm:tw-p-5">

                    <div className="tw-w-full tw-max-w-[390px] tw-overflow-hidden tw-rounded-[24px] tw-border tw-border-white/80 tw-bg-[#F8FAFC] tw-shadow-[0_20px_52px_rgba(15,23,42,0.10)]">

                        <div

                            className={`tw-h-[3px] tw-w-full ${

                                status.error

                                    ? "tw-bg-rose-500"

                                    : "tw-bg-gradient-to-r tw-from-blue-500 tw-via-violet-500 tw-to-emerald-400"

                            }`}

                        />



                        <div

                            className="tw-px-5 tw-py-4"

                            style={{

                                background: "#102A35",

                            }}

                        >

                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">

                                <img

                                    src={currentLogo}

                                    alt="iParking"

                                    className="tw-w-[98px] tw-object-contain"

                                />



                                {!status.error && (

                                    <span className="tw-rounded-full tw-border tw-border-blue-300/20 tw-bg-blue-300/10 tw-px-2.5 tw-py-1.5 tw-text-[8.5px] tw-font-medium tw-text-blue-100">

                                        Түр хүлээнэ үү

                                    </span>

                                )}

                            </div>

                        </div>



                        <div className="tw-flex tw-flex-col tw-items-center tw-px-5 tw-py-9 tw-text-center">

                            {status.loading && (

                                <div className="tw-relative tw-h-12 tw-w-12">

                                    <div className="tw-relative tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-[3px] tw-border-slate-200 tw-border-t-[#16836F]" />

                                </div>

                            )}



                            {status.error && !status.loading && (

                                <button

                                    type="button"

                                    onClick={() => {
                                                if (paymentIntent === "pay") setSafeCheckContext(contextKey);
                                                setInitializationRetry((value) => value + 1);
                                            }}

                                    className="tw-mt-4 tw-h-11 tw-w-full tw-rounded-[13px] tw-bg-[#07966E] tw-text-[11px] tw-font-semibold tw-text-white"

                                >

                                    Дахин шалгах

                                </button>

                            )}

                            {status.error && !status.loading && (

                                <div className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-rose-50 tw-text-rose-500">

                                    <MdErrorOutline className="tw-text-[29px]" />

                                </div>

                            )}



                            <h2 className="tw-mt-4 tw-mb-0 tw-text-[17px] tw-font-bold tw-text-slate-900">

                                {status.loading ? "Мэдээлэл шалгаж байна" : "Алдаа гарлаа"}

                            </h2>



                            <p

                                className={`tw-mx-auto tw-mt-1.5 tw-mb-0 tw-max-w-[280px] tw-text-[10.5px] tw-leading-[1.6] ${

                                    status.error ? "tw-text-rose-600" : "tw-text-slate-500"

                                }`}

                            >

                                {status.loading

                                    ? "Төлбөрийн мэдээллийг түр хүлээнэ үү."

                                    : status.error}

                            </p>



                            {status.error && !status.loading && (

                                <button

                                    type="button"

                                    onClick={() => navigate(`/plate/${config.parkId}`)}

                                    className="tw-mt-5 tw-h-11 tw-w-full tw-rounded-[13px] tw-border tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-semibold tw-text-slate-600"

                                >

                                    Буцах

                                </button>

                            )}

                        </div>

                    </div>

                </div>

            </div>

        );

    }



    // ----------------------------------------------------

    // 💳 ТӨЛБӨР ТӨЛӨХ ҮНДСЭН ДЭЛГЭЦ

    // ----------------------------------------------------

    return (

        <div

            className="tw-fixed tw-inset-0 tw-z-[99999] tw-overflow-y-auto tw-overscroll-contain tw-bg-[#F1F6F5]"

            style={{

                fontFamily: UI_FONT,

                backgroundImage:

                    "radial-gradient(circle at 0% 0%, rgba(104,220,185,0.14), transparent 42%), radial-gradient(circle at 100% 95%, rgba(81,157,184,0.07), transparent 50%)",

            }}

        >

            <div className="tw-min-h-full tw-w-full tw-px-3 tw-py-3.5 sm:tw-flex sm:tw-items-center sm:tw-justify-center sm:tw-px-5 sm:tw-py-7">

                <main className="tw-mx-auto tw-w-full tw-max-w-[450px]">

                    <section className="tw-overflow-hidden tw-rounded-[28px] tw-border tw-border-white tw-bg-white tw-shadow-[0_18px_65px_rgba(16,42,50,0.11)]">

                        



                        <header className="tw-bg-[#102A35] tw-px-5 tw-pt-5 tw-pb-5 sm:tw-px-7">
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                                <img src={currentLogo} alt="iParking" className="tw-w-[125px] tw-object-contain" />
                                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-white/15 tw-bg-white/10 tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-semibold tw-text-[#E6FFF5]">
                                    <MdAccessTime size={13} className="tw-text-[#6DEAC2]" />
                                    {config.duration}
                                </span>
                            </div>
                            <div className="tw-mt-5 tw-flex tw-gap-2" aria-label=" 3: Төлбөр төлөх">
                                <span className="tw-h-[5px] tw-flex-1 tw-rounded-full tw-bg-[#4AE0B1]" />
                                <span className="tw-h-[5px] tw-flex-1 tw-rounded-full tw-bg-[#4AE0B1]" />
                                <span className="tw-h-[5px] tw-flex-1 tw-rounded-full tw-bg-[#4AE0B1]" />
                            </div>
                            <p className="tw-mt-3 tw-mb-0 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-[0.14em] tw-text-[#7DE5BF]"> 3 / 3</p>
                            <h1 className="tw-mt-1 tw-mb-1 tw-text-[26px] tw-font-bold tw-leading-[1.2] tw-tracking-[-0.035em] tw-text-white">
                                Төлбөр төлөх
                            </h1>
                            <p className="tw-m-0 tw-text-[12px] tw-leading-5 tw-text-slate-300">
                                {payment.invoice && !payment.needsRefresh
                                    ? "QR код уншуулах эсвэл банкны апп сонгоно уу."
                                    : autoPay
                                        ? "Таны машины QR төлбөрийг автоматаар бэлдэж байна."
                                        : "Төлбөрийн мэдээллээ шалгана уу."}
                            </p>
                        </header>



                        <div className="tw-p-4 sm:tw-p-5">

                            {/* Selected vehicle and amount: the two things a driver checks first. */}
                            <div className="tw-rounded-[20px] tw-border tw-border-[#E6EFF0] tw-bg-[#FAFCFC] tw-p-4 sm:tw-p-5">
                                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                                    <div className="tw-min-w-0">
                                        <p className="tw-m-0 tw-text-[11px] tw-font-semibold tw-text-[#627986]">Улсын дугаар</p>
                                        <p className="tw-mt-1 tw-mb-0 tw-text-[25px] tw-font-extrabold tw-tracking-[0.04em] tw-text-[#102A32] ">
                                            {recordData.plateNumber}
                                        </p>
                                    </div>
                                    <span className="tw-shrink-0 tw-rounded-full tw-bg-[#DCF5E9] tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-bold tw-text-[#087457]">
                                        IPARKING
                                    </span>
                                </div>
                                <div className="tw-my-4  tw-h-px tw-bg-[#E5ECED]" />
                                <p className="tw-m-0 tw-text-[11px] tw-font-semibold tw-text-[#627986] tw-text-center">Төлөх нийт дүн</p>
                                <p className="tw-mt-1 tw-mb-0 tw-text-[39px] tw-font-extrabold tw-leading-[1.15] tw-tracking-[-0.04em] tw-text-[#0A745A] tw-tabular-nums  tw-text-center">
                                    {formatPrice(recordData.currentPrice)}
                                </p>
                                <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-2.5">
                                    <div className="tw-rounded-[13px] tw-bg-white tw-px-3 tw-py-2.5 tw-shadow-[0_1px_4px_rgba(16,42,50,0.04)]">
                                        <span className="tw-block tw-text-[10px] tw-font-semibold tw-text-[#758A94]">Орсон цаг</span>
                                        <span className="tw-mt-1 tw-block tw-text-[11px] tw-font-semibold tw-leading-5 tw-text-[#233D47]">{recordData.entryTime}</span>
                                    </div>
                                    <div className="tw-rounded-[13px] tw-bg-white tw-px-3 tw-py-2.5 tw-shadow-[0_1px_4px_rgba(16,42,50,0.04)]">
                                        <span className="tw-block tw-text-[10px] tw-font-semibold tw-text-[#758A94]">Тооцсон цаг</span>
                                        <span className="tw-mt-1 tw-block tw-text-[11px] tw-font-semibold tw-leading-5 tw-text-[#233D47]">{recordData.exitTime}</span>
                                    </div>
                                </div>
                            </div>

                            {status.notice && (

                                <p

                                    role="status"

                                    className="tw-mt-3 tw-rounded-[12px] tw-bg-amber-50 tw-p-3 tw-text-[11px] tw-leading-5 tw-text-amber-950"

                                >

                                    {status.notice}

                                </p>

                            )}

                            {/* QR is requested automatically after a plate-list selection.
                                The only optional action with an existing invoice is to check status. */}
                            {((payment.invoice && (!autoPay || payment.needsRefresh)) || paymentIntent === "preview") && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (payment.invoice) {
                                            // Never issue a new invoice while the previous one may be pending.
                                            setCheckRequest((value) => value + 1);
                                        } else {
                                            // Explicit manual fallback for direct links or a safely rechecked request.
                                            setSafeCheckContext(null);
                                            setPaymentRequest(contextKey);
                                            setInitializationRetry((value) => value + 1);
                                        }
                                    }}
                                    className={payment.invoice
                                        ? "tw-mt-3 tw-h-10 tw-w-full tw-rounded-[13px] tw-border tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-semibold tw-text-slate-700"
                                        : "tw-mt-3 tw-h-11 tw-w-full tw-rounded-[13px] tw-bg-[#16836F] tw-text-[11px] tw-font-semibold tw-text-white"}
                                >
                                    {payment.invoice ? "Төлбөр шалгах" : "Төлөх"}
                                </button>
                            )}
                            {autoPay && payment.invoice && !payment.needsRefresh && (
                                <p className="tw-mt-3 tw-text-center tw-text-[11px] tw-font-medium tw-text-slate-500">
                                    Төлбөрийн баталгааг автоматаар шалгаж байна
                                </p>
                            )}
                            {/* Exceptional case: an auto-invoice request returned no invoice.
                                Query server state without making a second charge request. */}
                            {!payment.invoice && paymentIntent === "pay" && !status.complete && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSafeCheckContext(contextKey);
                                        setInitializationRetry((value) => value + 1);
                                    }}
                                    className="tw-mt-3 tw-h-10 tw-w-full tw-rounded-[13px] tw-border tw-border-amber-200 tw-bg-amber-50 tw-text-[11px] tw-font-semibold tw-text-amber-900"
                                >
                                    Нэхэмжлэлийн төлөв шалгах
                                </button>
                            )}
                            {!payment.invoice && paymentIntent === "preview" && (
                                <button
                                    type="button"
                                    onClick={() => setInitializationRetry((value) => value + 1)}
                                    className="tw-mt-2 tw-h-10 tw-w-full tw-rounded-[13px] tw-border tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-semibold tw-text-slate-600"
                                >
                                    Дүн шинэчлэх
                                </button>
                            )}

                            <AnimatePresence mode="wait">

                                {status.invoiceLoading ? (

                                    <motion.div

                                        key="loading"

                                        initial={{ opacity: 0 }}

                                        animate={{ opacity: 1 }}

                                        exit={{ opacity: 0 }}

                                        className="tw-flex tw-min-h-[250px] tw-w-full tw-flex-col tw-items-center tw-justify-center"

                                    >

                                        <div className="tw-relative tw-h-11 tw-w-11">

                                            <div className="tw-relative tw-h-11 tw-w-11 tw-animate-spin tw-rounded-full tw-border-[3px] tw-border-slate-200 tw-border-t-[#16836F]" />

                                        </div>



                                        <p className="tw-mt-3 tw-mb-0 tw-text-[10px] tw-font-medium tw-text-slate-500">

                                            Нэхэмжлэх үүсгэж байна...

                                        </p>

                                    </motion.div>

                                ) : (

                                    <motion.div

                                        key="content"

                                        initial={{ opacity: 0, y: 6 }}

                                        animate={{ opacity: 1, y: 0 }}

                                        className="tw-w-full"

                                    >

                                        {payment.mainQr && (
                                            <div className="tw-mt-3 tw-overflow-hidden tw-rounded-[20px] tw-border tw-border-[#D9EBE4] tw-bg-white tw-shadow-[0_7px_20px_rgba(16,42,50,0.045)]">
                                                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-bg-[#EDFAF4] tw-px-4 tw-py-3.5">
                                                    <div>
                                                        <span className="tw-block tw-text-[15px] tw-font-bold tw-text-[#103C35]">QR кодоор төлөх</span>
                                                        <span className="tw-mt-0.5 tw-block tw-text-[11px] tw-text-[#527268]">Банкны апп-аар уншуулна уу</span>
                                                    </div>
                                                    <span className="tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5 tw-text-[10px] tw-font-bold tw-text-[#087457]"> QR</span>
                                                </div>
                                                <div className="tw-flex tw-flex-col tw-items-center tw-px-4 tw-pt-4 tw-pb-5">
                                                    <div className="tw-rounded-[18px] tw-border tw-border-[#E2ECEA] tw-bg-white tw-p-3 tw-shadow-[0_6px_18px_rgba(16,42,50,0.07)]">
                                                        <QRCodeCanvas value={payment.mainQr} size={220} includeMargin={false} title="Minu QR төлбөр" />
                                                    </div>
                                                    <p className="tw-mt-3 tw-mb-0 tw-text-center tw-text-[12px] tw-font-medium tw-text-[#607784]">
                                                        Төлбөр төлөгдвөл автоматаар баталгаажна.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {payment.banks.length > 0 && (

                                            <div className="tw-mt-3 tw-rounded-[20px] tw-border tw-border-[#E6EFF0] tw-bg-white tw-p-4">

                                                <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">

                                                    <div>

                                                        <span className="tw-block tw-text-[15px] tw-font-bold tw-text-[#102A32]">

                                                            Банкны апп

                                                        </span>

                                                        <span className="tw-mt-0.5 tw-block tw-text-[11px] tw-text-slate-500">

                                                            Төлөх банкаа сонгоно уу

                                                        </span>

                                                    </div>



                                                    <span className="tw-rounded-full tw-bg-emerald-50 tw-px-2 tw-py-1 tw-text-[8px] tw-font-semibold tw-text-[#16836F]">

                                                        {payment.banks.length}

                                                    </span>

                                                </div>



                                                <div className="tw-grid tw-grid-cols-3 tw-gap-2.5">

                                                    {payment.banks.map((bank, i) => {

                                                        const imageSrc =

                                                            bank.deeplinkName === "Khan bank"

                                                                ? "https://play-lh.googleusercontent.com/PFiYIqgKKgiq7xN1pYaQw5XpTnm64BDdsFjm106vyq-M2GAVPW_ajQKI2cGKKl9573o"

                                                                : bank.deeplinkName === "State bank"

                                                                  ? "https://play-lh.googleusercontent.com/QCG5vwYkrmGXMipDBA24LgTYldrtBpS-2A5BQLv9IudF55MnNhx7zJBlHK9ruQVZK9RX"

                                                                  : bank.deeplinkName === "Xac bank"

                                                                    ? "https://play-lh.googleusercontent.com/vHTV4z9QgeW9tPT2uIQtFIFA1Y_i2se3mrc98h4XvUJFhvQGgLRlCdl4oHF3YZXI4qcG"

                                                                    : bank.deeplinkName ===

                                                                        "Trade and Development bank"

                                                                      ? "https://play-lh.googleusercontent.com/oLKxnYGEgAyoxZ4rG6ogqzoLQMIUC7wrAuE7tca9PKWZubIev1t3CvvnJvpvj7KhKL4"

                                                                      : bank.deeplinkName ===

                                                                          "Chinggis khaan bank"

                                                                        ? "https://play-lh.googleusercontent.com/FWX4zrThtk1vxrWYMtJF-PU8muqo3RTv09eLFRUKc1PEyGVsIP3QhHdmiK0-0KNhTy9s=w480-h960-rw"

                                                                        : bank.deeplinkName ===

                                                                            "Capitron bank"

                                                                          ? "https://play-lh.googleusercontent.com/k_a9qZEO8IH3fPgD_YW68OFqri3h54cnUbVC9EujJMjginrUMFPsDyY6e4WcJSC1uFk=w480-h960-rw"

                                                                          : bank.deeplinkName ===

                                                                              "Bogd bank"

                                                                            ? "https://play-lh.googleusercontent.com/owtR0m1iTmhVTknI0FcapCgWJswPAVFvzC3eZUZLMwaDaMjMFB7gj0KGI6jvZGtBUfs=w480-h960-rw"

                                                                            : bank.deeplinkName ===

                                                                                "MonPay"

                                                                              ? "https://play-lh.googleusercontent.com/GofyFzRM2Kwf3d47fl6FibZB7kE16Aljaodzc-ghiJmdiPpGljaqeop2T6JaURd8rw=s96-rw"

                                                                              : bank.deeplinkName ===

                                                                                  "SocialPay"

                                                                                ? "https://play-lh.googleusercontent.com/Jg_jjsNezlkTuxWT5ADzfqhjwHVvqZEDqQGbXJlkplNrYPyyMGXtmLA6dGrH37_paOY=w480-h960-rw"

                                                                                : bank.deeplinkName ===

                                                                                    "National investment bank"

                                                                                  ? "https://upload.wikimedia.org/wikipedia/commons/d/d7/Nibank-logo_%281%29.jpg?20141215081525"

                                                                                  : `https://cdn.minu.mn/img/${bank.image}`;



                                                        return (

                                                            <button

                                                                type="button"

                                                                key={i}

                                                                onClick={() => {

                                                                    if (bank.deeplinkLink) {

                                                                        window.location.href =

                                                                            bank.deeplinkLink;

                                                                    }

                                                                }}

                                                                className={`tw-flex tw-min-h-[94px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-[14px] tw-border tw-p-2 tw-transition-all hover:tw--translate-y-0.5 hover:tw-shadow-[0_8px_18px_rgba(59,130,246,0.10)] active:tw-scale-[0.98] ${bankTone(i)}`}

                                                            >

                                                                <img

                                                                    src={imageSrc}

                                                                    alt={

                                                                        bank.deeplinkName ||

                                                                        bank.deeplinkDesc

                                                                    }

                                                                    className="tw-h-10 tw-w-10 tw-rounded-[12px] tw-border tw-border-white tw-bg-white tw-object-contain tw-shadow-sm"

                                                                />



                                                                <span className="tw-line-clamp-2 tw-w-full tw-text-center tw-text-[10.5px] tw-font-semibold tw-leading-[1.3] tw-text-slate-700">

                                                                    {bank.deeplinkName ||

                                                                        bank.deeplinkDesc}

                                                                </span>

                                                            </button>

                                                        );

                                                    })}

                                                </div>

                                            </div>

                                        )}

                                    </motion.div>

                                )}

                            </AnimatePresence>



                            <div className="tw-mt-3 tw-flex tw-items-start tw-gap-2.5 tw-rounded-[12px] tw-border tw-border-amber-200/70 tw-bg-amber-50/65 tw-px-3 tw-py-2.5">

                                <span className="tw-mt-[2px] tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-500 tw-text-[9px] tw-font-bold tw-text-white">

                                    !

                                </span>



                                <p className="tw-m-0 tw-text-[11px] tw-leading-[1.55] tw-text-amber-950">

                                    Төлбөр төлснөөс хойш{" "}

                                    <strong className="tw-font-semibold">

                                        {config.gracePeriod} минутын

                                    </strong>{" "}

                                    дотор зогсоолоос гарна уу.

                                </p>

                            </div>



                            <button

                                type="button"

                                onClick={() => navigate(`/plate/${config.parkId}`)}

                                className="tw-mt-3 tw-h-12 tw-w-full tw-rounded-[15px] tw-border tw-border-[#DDE8EA] tw-bg-white tw-text-[12px] tw-font-semibold tw-text-[#4A6470] tw-transition hover:tw-border-blue-200 hover:tw-bg-blue-50/40 active:tw-scale-[0.99]"

                            >

                                Буцах

                            </button>



                            <AppFooter />

                        </div>

                    </section>

                </main>

            </div>

        </div>

    );

};



export default ParkingPayment;
